import {
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { PartOrder } from 'app/models/PartOrder';
import { AppRootState } from 'index';

const partOrderAdapter = createEntityAdapter<PartOrder>({
  selectId: partOrder => partOrder.id,
});

const preserveLoadedDetails = (
  incomingPartOrder: PartOrder,
  existingPartOrder?: PartOrder,
): PartOrder => {
  if (!existingPartOrder) return incomingPartOrder;

  return {
    ...incomingPartOrder,
    products:
      incomingPartOrder.products !== undefined
        ? incomingPartOrder.products
        : existingPartOrder.products,
    packages:
      incomingPartOrder.packages !== undefined
        ? incomingPartOrder.packages
        : existingPartOrder.packages,
    trolleys:
      incomingPartOrder.packages !== undefined ||
      incomingPartOrder.products !== undefined
        ? incomingPartOrder.trolleys
        : existingPartOrder.trolleys,
    siblings:
      incomingPartOrder.siblings !== undefined
        ? incomingPartOrder.siblings
        : existingPartOrder.siblings,
    cabinets:
      incomingPartOrder.cabinets !== undefined
        ? incomingPartOrder.cabinets
        : existingPartOrder.cabinets,
  };
};

const partOrderSlice = createSlice({
  name: 'partOrder',
  initialState: partOrderAdapter.getInitialState(),
  reducers: {
    setPartOrders(state, { payload }) {
      const mergedPartOrders = payload.map((partOrder: PartOrder) => {
        const existingPartOrder = state.entities[partOrder.id] as
          | PartOrder
          | undefined;
        return preserveLoadedDetails(partOrder, existingPartOrder);
      });
      partOrderAdapter.setAll(state, mergedPartOrders);
    },
    setPartOrder(state, { payload }) {
      const existingPartOrder = state.entities[payload.id] as
        | PartOrder
        | undefined;
      partOrderAdapter.setOne(
        state,
        preserveLoadedDetails(payload, existingPartOrder),
      );
    },
    addPartOrders(state, { payload }) {
      partOrderAdapter.addMany(state, payload);
    },
  },
});

export const {
  setPartOrders,
  setPartOrder,
  addPartOrders,
} = partOrderSlice.actions;

export const {
  selectById: selectPartOrderById,
  selectIds: selectPartOrderIds,
  selectEntities: selectPartOrderEntities,
  selectAll: selectAllPartOrders,
} = partOrderAdapter.getSelectors((state: AppRootState) => state.partOrder);

export const selectIsOdooOrderByPartOrderId = (partOrderId: number) =>
  createSelector(
    (state: AppRootState) => selectPartOrderById(state, partOrderId),
    partOrder => partOrder?.order?.source === 'odoo',
  );

export default partOrderSlice.reducer;
